import React, { useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { updateContent, clearToken } from "../../api";
import { useContent } from "../../context/ContentContext";
import UploadButton from "./UploadButton";

const DEFAULT_TRUST = {
  enabled: false,
  kicker: "Trusted By",
  title: "40+ brands worldwide",
  brands: [],
  testimonials: [
    { quote: "", author: "", role: "" },
    { quote: "", author: "", role: "" },
    { quote: "", author: "", role: "" },
  ],
};

/* ---------- shared bits ---------- */
export function Field({ label, value, onChange, textarea = false, rows = 3, testId }) {
  const cls =
    "bg-[#0d0d0d] border-white/25 text-white font-comfortaa text-sm placeholder:text-white/35";
  return (
    <div className="mb-4">
      <label className="font-comfortaa text-xs text-white/70 block mb-1.5">{label}</label>
      {textarea ? (
        <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId} />
      )}
    </div>
  );
}

export function Panel({ title, children }) {
  return (
    <div className="bg-[#1c1c1c] border border-white/10 rounded-lg p-6 mb-6">
      <h3 className="font-comfortaa text-white text-sm mb-5 tracking-wide uppercase opacity-80">{title}</h3>
      {children}
    </div>
  );
}

export function SaveButton({ onClick, busy, testId }) {
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      className="bg-white text-black hover:bg-white/85 font-comfortaa text-xs"
      data-testid={testId}
    >
      <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save changes"}
    </Button>
  );
}

async function saveSection(payload, setBusy, onSaved) {
  setBusy(true);
  try {
    await updateContent(payload);
    toast.success("Saved! Changes are live.");
    onSaved && onSaved();
  } catch (err) {
    if (err?.response?.status === 401) {
      clearToken();
      toast.error("Session expired — please sign in again.");
      window.location.reload();
    } else {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  } finally {
    setBusy(false);
  }
}

/* ---------- Hero & Home ---------- */
export function HeroHomeEditor({ onSaved }) {
  const { content } = useContent();
  const [hero, setHero] = useState({
    ...content.hero,
    videosText: content.hero.videos.join("\n"),
    cutSeconds: content.hero.cutSeconds ?? 2,
    lastCutSeconds: content.hero.lastCutSeconds ?? 3,
  });
  const [home, setHome] = useState({ ...content.home });
  const [brand, setBrand] = useState({ ...content.brand });
  const [trust, setTrust] = useState(() => {
    const t = { ...DEFAULT_TRUST, ...(content.trust || {}) };
    return { ...t, brandsText: (t.brands || []).join("\n") };
  });
  const [busy, setBusy] = useState(false);

  const setTestimonial = (i, key, v) => {
    const testimonials = trust.testimonials.map((t, idx) => (idx === i ? { ...t, [key]: v } : t));
    setTrust({ ...trust, testimonials });
  };

  const save = () => {
    const { brandsText, ...trustRest } = trust;
    saveSection(
      {
        hero: {
          title: hero.title,
          subtitle: hero.subtitle,
          videos: hero.videosText.split("\n").map((s) => s.trim()).filter(Boolean),
          cutSeconds: Math.max(0.5, Number(hero.cutSeconds) || 2),
          lastCutSeconds: Math.max(0.5, Number(hero.lastCutSeconds) || 3),
        },
        home,
        brand,
        trust: {
          ...trustRest,
          brands: brandsText.split("\n").map((s) => s.trim()).filter(Boolean),
        },
      },
      setBusy,
      onSaved
    );
  };

  return (
    <div data-testid="editor-hero-home">
      <Panel title="Brand">
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Logo text (caps)" value={brand.namePrimary} onChange={(v) => setBrand({ ...brand, namePrimary: v })} testId="brand-name-input" />
          <Field label="Logo script word" value={brand.nameScript} onChange={(v) => setBrand({ ...brand, nameScript: v })} />
        </div>
        <Field label="Footer copyright" value={brand.copyright} onChange={(v) => setBrand({ ...brand, copyright: v })} />
      </Panel>

      <Panel title="Hero">
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Hero title" value={hero.title} onChange={(v) => setHero({ ...hero, title: v })} testId="hero-title-input" />
          <Field label="Hero subtitle" value={hero.subtitle} onChange={(v) => setHero({ ...hero, subtitle: v })} />
        </div>
        <Field
          label="Background videos — one URL per line (they play one after another)"
          value={hero.videosText}
          onChange={(v) => setHero({ ...hero, videosText: v })}
          textarea
          rows={5}
          testId="hero-videos-input"
        />
        <div className="flex items-center gap-3 mb-5 -mt-1">
          <UploadButton
            label="Upload & add to playlist"
            testId="hero-video-upload"
            onUploaded={(url) =>
              setHero((prev) => ({
                ...prev,
                videosText: (prev.videosText.trim() ? prev.videosText.trim() + "\n" : "") + url,
              }))
            }
          />
          <span className="font-comfortaa text-white/40 text-[11px]">
            mp4 / webm / mov, up to 300MB — added as a new line above
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-x-6">
          <div className="mb-4">
            <label className="font-comfortaa text-xs text-white/70 block mb-1.5">
              Cut speed per clip (seconds)
            </label>
            <Input
              type="number"
              step="0.5"
              min="0.5"
              value={hero.cutSeconds}
              onChange={(e) => setHero({ ...hero, cutSeconds: e.target.value })}
              className="bg-[#0d0d0d] border-white/25 text-white font-comfortaa text-sm"
              data-testid="hero-cut-seconds-input"
            />
          </div>
          <div className="mb-4">
            <label className="font-comfortaa text-xs text-white/70 block mb-1.5">
              Hold on last clip (seconds)
            </label>
            <Input
              type="number"
              step="0.5"
              min="0.5"
              value={hero.lastCutSeconds}
              onChange={(e) => setHero({ ...hero, lastCutSeconds: e.target.value })}
              className="bg-[#0d0d0d] border-white/25 text-white font-comfortaa text-sm"
              data-testid="hero-last-cut-seconds-input"
            />
          </div>
        </div>
        <p className="font-comfortaa text-white/40 text-[11px] -mt-1">
          The hero cuts between clips at this speed, holding the last clip a beat longer before looping.
        </p>
      </Panel>

      <Panel title="Homepage sections">
        <Field label="Intro paragraph" value={home.intro} onChange={(v) => setHome({ ...home, intro: v })} textarea rows={4} testId="home-intro-input" />
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Sample work section title" value={home.sampleWorkTitle} onChange={(v) => setHome({ ...home, sampleWorkTitle: v })} />
          <Field label="Portfolio button label" value={home.portfolioButton} onChange={(v) => setHome({ ...home, portfolioButton: v })} />
        </div>
        <Field label="Team statement" value={home.teamStatement} onChange={(v) => setHome({ ...home, teamStatement: v })} textarea rows={4} />
        <Field label="Collage image URL" value={home.collageImage} onChange={(v) => setHome({ ...home, collageImage: v })} />
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="CTA title" value={home.ctaTitle} onChange={(v) => setHome({ ...home, ctaTitle: v })} />
          <Field label="CTA button label" value={home.ctaButton} onChange={(v) => setHome({ ...home, ctaButton: v })} />
        </div>
      </Panel>

      <Panel title="Trust section (Trusted by brands + testimonials)">
        <div className="flex items-center gap-3 mb-6">
          <Switch
            checked={trust.enabled}
            onCheckedChange={(c) => setTrust({ ...trust, enabled: c })}
            data-testid="trust-enabled-switch"
          />
          <span className="font-comfortaa text-xs text-white/80">
            {trust.enabled ? "Visible on homepage" : "Hidden from homepage (flip to go live)"}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Kicker (small label)" value={trust.kicker} onChange={(v) => setTrust({ ...trust, kicker: v })} />
          <Field label="Title" value={trust.title} onChange={(v) => setTrust({ ...trust, title: v })} testId="trust-title-input" />
        </div>
        <Field
          label="Brand names — one per line"
          value={trust.brandsText}
          onChange={(v) => setTrust({ ...trust, brandsText: v })}
          textarea
          rows={4}
          testId="trust-brands-input"
        />
        {trust.testimonials.map((t, i) => (
          <div key={i} className="border-t border-white/10 pt-4 mt-2">
            <Field label={`Testimonial ${i + 1} — quote`} value={t.quote} onChange={(v) => setTestimonial(i, "quote", v)} textarea rows={2} />
            <div className="grid md:grid-cols-2 gap-x-6">
              <Field label="Author" value={t.author} onChange={(v) => setTestimonial(i, "author", v)} />
              <Field label="Role / company" value={t.role} onChange={(v) => setTestimonial(i, "role", v)} />
            </div>
          </div>
        ))}
      </Panel>

      <SaveButton onClick={save} busy={busy} testId="save-hero-home" />
    </div>
  );
}

/* ---------- About ---------- */
export function AboutEditor({ onSaved }) {
  const { content } = useContent();
  const [about, setAbout] = useState({
    ...content.about,
    toolsText: (content.about.tools || []).join(", "),
  });
  const [busy, setBusy] = useState(false);

  const setStat = (i, key, v) => {
    const stats = about.stats.map((s, idx) => (idx === i ? { ...s, [key]: v } : s));
    setAbout({ ...about, stats });
  };
  const setService = (i, key, v) => {
    const services = about.services.map((s, idx) => (idx === i ? { ...s, [key]: v } : s));
    setAbout({ ...about, services });
  };

  const save = () => {
    const { toolsText, ...rest } = about;
    saveSection({ about: { ...rest, tools: toolsText.split(",").map((s) => s.trim()).filter(Boolean) } }, setBusy, onSaved);
  };

  return (
    <div data-testid="editor-about">
      <Panel title="Page header">
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Heading" value={about.heading} onChange={(v) => setAbout({ ...about, heading: v })} />
          <Field label="Studio image URL" value={about.studioImage} onChange={(v) => setAbout({ ...about, studioImage: v })} />
        </div>
        <Field label="Intro" value={about.intro} onChange={(v) => setAbout({ ...about, intro: v })} textarea />
      </Panel>

      <Panel title="Story">
        <Field label="Story paragraph 1" value={about.story1} onChange={(v) => setAbout({ ...about, story1: v })} textarea rows={4} />
        <Field label="Story paragraph 2" value={about.story2} onChange={(v) => setAbout({ ...about, story2: v })} textarea rows={4} />
      </Panel>

      <Panel title="Stats">
        {about.stats.map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-x-4">
            <Field label={`Stat ${i + 1} value`} value={s.value} onChange={(v) => setStat(i, "value", v)} />
            <Field label={`Stat ${i + 1} label`} value={s.label} onChange={(v) => setStat(i, "label", v)} />
          </div>
        ))}
      </Panel>

      <Panel title="Services">
        {about.services.map((s, i) => (
          <div key={i} className="border-b border-white/10 pb-2 mb-4">
            <Field label={`Service ${i + 1} name`} value={s.name} onChange={(v) => setService(i, "name", v)} />
            <Field label="Description" value={s.desc} onChange={(v) => setService(i, "desc", v)} textarea rows={2} />
          </div>
        ))}
      </Panel>

      <Panel title="AI models / tools (comma separated)">
        <Field label="Tools" value={about.toolsText} onChange={(v) => setAbout({ ...about, toolsText: v })} textarea rows={3} />
      </Panel>

      <SaveButton onClick={save} busy={busy} testId="save-about" />
    </div>
  );
}

/* ---------- Contact & Footer ---------- */
export function ContactFooterEditor({ onSaved }) {
  const { content } = useContent();
  const [contact, setContact] = useState({ ...content.contact });
  const [footer, setFooter] = useState({ ...content.footer });
  const [socials, setSocials] = useState([...content.socials]);
  const [portfolio, setPortfolio] = useState({ ...content.portfolio });
  const [busy, setBusy] = useState(false);

  const setSocial = (i, key, v) => {
    setSocials(socials.map((s, idx) => (idx === i ? { ...s, [key]: v } : s)));
  };

  const save = () => saveSection({ contact, footer, socials, portfolio }, setBusy, onSaved);

  return (
    <div data-testid="editor-contact-footer">
      <Panel title="Contact page">
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Heading" value={contact.heading} onChange={(v) => setContact({ ...contact, heading: v })} testId="contact-heading-input" />
          <Field label="Form note" value={contact.formNote} onChange={(v) => setContact({ ...contact, formNote: v })} />
        </div>
        <Field label="Intro" value={contact.intro} onChange={(v) => setContact({ ...contact, intro: v })} textarea />
        <div className="grid md:grid-cols-2 gap-x-6">
          <Field label="Email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
          <Field label="Calendly / booking URL" value={contact.calendly} onChange={(v) => setContact({ ...contact, calendly: v })} />
        </div>
      </Panel>

      <Panel title="Portfolio page header">
        <Field label="Heading" value={portfolio.heading} onChange={(v) => setPortfolio({ ...portfolio, heading: v })} />
        <Field label="Intro" value={portfolio.intro} onChange={(v) => setPortfolio({ ...portfolio, intro: v })} textarea />
        <div className="grid md:grid-cols-3 gap-x-4">
          <Field label="Main section title" value={portfolio.sectionMain} onChange={(v) => setPortfolio({ ...portfolio, sectionMain: v })} />
          <Field label="Verticals section title" value={portfolio.sectionVerticals} onChange={(v) => setPortfolio({ ...portfolio, sectionVerticals: v })} />
          <Field label="Fun section title" value={portfolio.sectionFun} onChange={(v) => setPortfolio({ ...portfolio, sectionFun: v })} />
        </div>
      </Panel>

      <Panel title="Footer newsletter">
        <Field label="Title" value={footer.newsletterTitle} onChange={(v) => setFooter({ ...footer, newsletterTitle: v })} />
        <Field label="Text" value={footer.newsletterText} onChange={(v) => setFooter({ ...footer, newsletterText: v })} textarea />
        <Field label="Button label" value={footer.newsletterButton} onChange={(v) => setFooter({ ...footer, newsletterButton: v })} />
      </Panel>

      <Panel title="Social links">
        {socials.map((s, i) => (
          <div key={i} className="grid grid-cols-[140px_1fr] gap-x-4">
            <Field label="Platform" value={s.name} onChange={(v) => setSocial(i, "name", v)} />
            <Field label="URL" value={s.url} onChange={(v) => setSocial(i, "url", v)} />
          </div>
        ))}
      </Panel>

      <SaveButton onClick={save} busy={busy} testId="save-contact-footer" />
    </div>
  );
}
